<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { toIntlLocale } from 'shared/helpers/intlLocale';

import Button from 'dashboard/components-next/button/Button.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';

const props = defineProps({
  table: {
    type: Object,
    required: true,
  },
  defaultPageSize: {
    type: Number,
    default: 10,
  },
  showPageSizeSelector: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['pageSizeChange']);
const { t, locale } = useI18n();

const pageSizeOptions = [
  {
    label: `${t('REPORT.PAGINATION.PER_PAGE_TEMPLATE', { size: 10 })}`,
    value: 10,
  },
  {
    label: `${t('REPORT.PAGINATION.PER_PAGE_TEMPLATE', { size: 20 })}`,
    value: 20,
  },
  {
    label: `${t('REPORT.PAGINATION.PER_PAGE_TEMPLATE', { size: 50 })}`,
    value: 50,
  },
  {
    label: `${t('REPORT.PAGINATION.PER_PAGE_TEMPLATE', { size: 100 })}`,
    value: 100,
  },
];

const getFormattedPages = (start, end) => {
  const formatter = new Intl.NumberFormat(toIntlLocale(locale.value));
  return Array.from({ length: end - start + 1 }, (_, i) => ({
    number: start + i,
    label: formatter.format(start + i),
  }));
};

const currentPage = computed(() => {
  return props.table.getState().pagination.pageIndex + 1;
});

const totalPages = computed(() => {
  return props.table.getPageCount();
});

const visiblePages = computed(() => {
  if (totalPages.value <= 3) return getFormattedPages(1, totalPages.value);
  if (currentPage.value === 1) return getFormattedPages(1, 3);
  if (currentPage.value === totalPages.value) {
    return getFormattedPages(totalPages.value - 2, totalPages.value);
  }

  return getFormattedPages(currentPage.value - 1, currentPage.value + 1);
});

const total = computed(() => {
  return props.table.getRowCount();
});

const start = computed(() => {
  const { pagination } = props.table.getState();
  return pagination.pageIndex * pagination.pageSize + 1;
});

const end = computed(() => {
  const { pagination } = props.table.getState();
  return Math.min(
    (pagination.pageIndex + 1) * pagination.pageSize,
    total.value
  );
});

const currentPageSize = computed({
  get() {
    return props.table.getState().pagination.pageSize;
  },
  set(newValue) {
    props.table.setPageSize(Number(newValue));
    emit('pageSizeChange', Number(newValue));
  },
});

onMounted(() => {
  if (
    props.showPageSizeSelector &&
    props.defaultPageSize &&
    props.defaultPageSize !== 10
  ) {
    props.table.setPageSize(props.defaultPageSize);
  }
});
</script>

<template>
  <div class="w-full">
    <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <p class="text-sm text-n-slate-11 mb-0">
        {{ $t('REPORT.PAGINATION.RESULTS', { start, end, total }) }}
      </p>
      <div class="flex flex-wrap items-center justify-center gap-3 md:justify-end">
        <FilterSelect
          v-if="showPageSizeSelector"
          v-model="currentPageSize"
          variant="outline"
          hide-icon
          class="[&>button]:text-n-slate-11 [&>button]:hover:text-n-slate-12 [&>button]:min-h-11 [&>button]:rounded-xl"
          :options="pageSizeOptions"
        />
        <nav class="isolate flex flex-wrap items-center justify-center gap-1"
          :aria-label="$t('REPORT.PAGINATION.LABEL')">
          <Button
            icon="i-ri-arrow-left-double-line"
            :aria-label="$t('REPORT.PAGINATION.FIRST')"
            ghost
            slate
            sm
            class="!size-11 !rounded-xl"
            :disabled="!table.getCanPreviousPage()"
            @click="table.setPageIndex(0)"
          />
          <Button
            icon="i-ri-arrow-left-s-line"
            :aria-label="$t('REPORT.PAGINATION.PREVIOUS')"
            ghost
            slate
            sm
            class="!size-11 !rounded-xl"
            :disabled="!table.getCanPreviousPage()"
            @click="table.previousPage()"
          />
          <Button
            v-for="page in visiblePages"
            :key="page.number"
            xs
            outline
            :color="page.number === currentPage ? 'blue' : 'slate'"
            class="!h-11 min-w-11 !rounded-xl"
            :aria-current="page.number === currentPage ? 'page' : undefined"
            :aria-label="$t('REPORT.PAGINATION.PAGE', { page: page.label })"
            @click="table.setPageIndex(page.number - 1)"
          >
            <span
              class="text-center"
              :class="{ 'text-n-brand': page.number === currentPage }"
            >
              {{ page.label }}
            </span>
          </Button>
          <Button
            icon="i-ri-arrow-right-s-line"
            :aria-label="$t('REPORT.PAGINATION.NEXT')"
            ghost
            slate
            sm
            class="!size-11 !rounded-xl"
            :disabled="!table.getCanNextPage()"
            @click="table.nextPage()"
          />
          <Button
            icon="i-ri-arrow-right-double-line"
            :aria-label="$t('REPORT.PAGINATION.LAST')"
            ghost
            slate
            sm
            class="!size-11 !rounded-xl"
            :disabled="!table.getCanNextPage()"
            @click="table.setPageIndex(table.getPageCount() - 1)"
          />
        </nav>
      </div>
    </div>
  </div>
</template>
